// [목적] /festivals/:id — 축제 상세 화면 (GET /festivals/:id 단건 조회 → 로딩 / 404 / 오류 / 정상 4분기)
// [이유] 디자인 개편 시안 "강원 축제·상세" 아트보드 반영. 이전에는 목록(limit 최대 36)을 받아 find 하는 우회라
//        목록 밖으로 밀린 축제는 직접 URL·새로고침·공유 링크로 열리지 않았다. 백엔드 단건 엔드포인트가 생겨
//        useFestival(id) 로 진입 경로와 무관하게 동일하게 조회한다(festival-detail-api Spec §D-5).
// [사이드 이팩트] 목록 화면에서 들어와도 목록 캐시를 재사용하지 않고 항상 상세 API 를 1회 요청한다(짧은 로딩 후 표시).
//               404(ApiError.status===404)는 "찾을 수 없음", 그 외 오류는 "재시도 안내"로 분리해 고정 카피만 노출한다
//               (서버 ApiError.message 는 화면에 노출하지 않음).
// [수정 시 고려사항] 오류 카피를 바꿀 때 404 / 그 외 분기 의미를 유지할 것(404 는 재시도해도 소용없음 → 목록 복귀 링크 제공).
//                   BE 응답 필드가 늘어나면 packages/types festivalItemSchema 가 SoT — 이 화면은 그 필드만 렌더.
// [수정: 2026-09-21] festival-comments — "이 축제로 여행 폴리오 만들기" CTA(to="/chat" 고정, 축제 맥락 미전달) 제거 +
//                   출처 주석 아래에 축제 댓글 섹션(FestivalCommentSection) 마운트. 댓글은 정상 렌더 분기에서만 마운트되어
//                   로딩/404/오류에서는 댓글 API 가 호출되지 않는다. 맥락 전달형 CTA 는 챗 슬롯 프리필 설계가 선행되어야 하므로 별도 Spec.
//                   ButtonBase import 제거(미사용). BRAND/PAPER0/alpha 는 다른 곳에서 계속 사용.

import { Box, Skeleton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { tokens } from '@gangwoncanvas/ui';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { FestivalCommentSection } from '../components/folio/CommentSection.js';
import { TopBar } from '../components/layout/TopBar.js';
import { useShell } from '../hooks/useShell.js';
import { useFestival } from '../hooks/useFestivals.js';
import { ApiError } from '../api/http.js';
import { FESTIVAL_STATUS_META } from '../utils/festivalStatusMeta.js';
import { safeCssUrl } from '../utils/safeCssUrl.js';
import { formatFestivalLocation, formatFestivalPeriodWithYear } from '../api/festivals.client.js';

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const INK_LOW = c.ink.low[L];
const BORDER = c.line.base[L];
const PAPER0 = c.paper[0][L];
const PAPER2 = c.paper[2][L];
const BRAND = c.brand[500][L];
const WARM = c.warm[500][L];

// [수정: 2026-09-20] 복붙되어 있던 상태 매핑을 공유 모듈로 교체(텍스트 대비 수정 포함).
const STATUS_META = FESTIVAL_STATUS_META;

export function FestivalDetailPage(): JSX.Element {
  const { viewport, openSidebar } = useShell();
  const { id } = useParams<{ id: string }>();
  const { data: festival, isLoading, error } = useFestival(id);
  const notFound = error instanceof ApiError && error.status === 404;

  return (
    <>
      <TopBar title="축제 상세" mobile={viewport.isMobile} onBurger={openSidebar} />
      <Box sx={{ p: { xs: '20px 16px 60px', md: '32px 32px 60px' }, maxWidth: 720, mx: 'auto' }}>
        <Typography
          component={RouterLink}
          to="/festivals"
          sx={{
            // [수정: 디자인 개편 2026-09-20] 12px 회색 맨텍스트라 링크로 인지되지 않았고
            // 클릭 영역도 글자 높이만큼 좁았다. 패딩 + hover 피드백을 준다.
            fontSize: 12.5,
            fontWeight: 600,
            color: INK_MID,
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            ml: '-8px',
            px: '8px',
            py: '5px',
            borderRadius: `${tokens.radius.s}px`,
            mb: '18px',
            transition: `background-color ${tokens.motion.fast.duration}ms, color ${tokens.motion.fast.duration}ms`,
            '&:hover': { bgcolor: PAPER2, color: INK_HI },
            '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
          }}
        >
          ← 강원 축제 목록으로
        </Typography>

        {isLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Skeleton variant="rounded" height={200} sx={{ borderRadius: '16px' }} />
            <Skeleton variant="text" width="60%" height={32} />
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="rounded" height={100} sx={{ borderRadius: '12px' }} />
          </Box>
        )}

        {!isLoading && (error || !festival) && (
          <Box
            role="alert"
            sx={{ p: '60px 20px', textAlign: 'center', color: INK_MID, fontSize: 13 }}
          >
            {notFound ? (
              <>
                <Box component="p" sx={{ m: 0, mb: 2 }}>
                  해당 축제를 찾을 수 없어요.
                </Box>
                <Typography
                  component={RouterLink}
                  to="/festivals"
                  aria-label="강원 축제 목록으로 돌아가기"
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: BRAND,
                    '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                  }}
                >
                  강원 축제 목록으로 돌아가기
                </Typography>
              </>
            ) : (
              '축제 정보를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.'
            )}
          </Box>
        )}

        {!isLoading && festival && (
          <>
            {/* [수정: 디자인 개편 2026-09-20] 히어로
                · 200 → 260px — 축제는 사진이 메인 콘텐츠인데 띄어쓰기보다 작은 띄로 떠 있었다.
                · 상태 배지를 이미지 위 오버레이로 — 목록 카드와 동일한 규칙으로 맞추고,
                  본문 한 줄을 통째로 쓰던 걸 회수한다. */}
            <Box sx={{ position: 'relative', mb: '20px' }}>
              <Box
                aria-hidden="true"
                sx={{
                  height: { xs: 200, md: 260 },
                  borderRadius: `${tokens.radius.l}px`,
                  overflow: 'hidden',
                  // [수정] url(${firstImage}) 직접 보간은 `)`·`;` 로 CSS 선언을 탈출할 수 있어 safeCssUrl 로 http(s) 가드 + 이스케이프(보안 Low-1)
                  background: safeCssUrl(festival.firstImage)
                    ? `${safeCssUrl(festival.firstImage)} center/cover`
                    : `linear-gradient(135deg, ${alpha(WARM, 0.3)}, ${alpha(BRAND, 0.18)})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 48,
                }}
              >
                {!safeCssUrl(festival.firstImage) && '🎪'}
              </Box>
              {(() => {
                const meta = STATUS_META[festival.status];
                return (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '12px',
                      left: '12px',
                      // [수정: 디자인 개편 2026-09-20] 배경을 alpha 13% 틴트로만 주면 사진 위에서
                      // 그대로 비치미션이 배경이 비칠 보여 배지가 사실상 안 보였다.
                      // paper 불투명 면 위에 동일한 틴트를 올려 "흰 배경 + 틴트" 와 똑같은 색을
                      // 유지하면서 불투명하게 만든다(색상 변경 없음).
                      bgcolor: PAPER0,
                      backgroundImage: `linear-gradient(${alpha(meta.color, 0.13)}, ${alpha(meta.color, 0.13)})`,
                      border: `1px solid ${alpha(meta.color, 0.3)}`,
                      // 11px 배지 텍스트 — 틴트 배경 위 AA 충족값(textColor) 사용
                      color: meta.textColor,
                      borderRadius: 99,
                      px: '10px',
                      py: '4px',
                      fontSize: 11,
                      lineHeight: 1.4,
                      fontWeight: 700,
                      boxShadow: `0 2px 8px ${alpha(INK_HI, 0.28)}`,
                    }}
                  >
                    {meta.label}
                  </Box>
                );
              })()}
            </Box>

            <Typography
              component="h1"
              sx={{
                // [수정: 디자인 개편 2026-09-20] 22 → 27px. 페이지의 주인공인데 바로 아래
                // 13px 메타·13px 본문과 대비가 약해 소제목처럼 보였다.
                fontSize: { xs: 23, md: 27 },
                fontWeight: 800,
                color: INK_HI,
                letterSpacing: '-0.8px',
                lineHeight: 1.28,
                mb: '12px',
              }}
            >
              {festival.title}
            </Typography>

            {/* [수정: 디자인 개편 2026-09-20] 기간·장소를 줄바꿈 두 줄 맨텍스트 → 칩 한 줄로.
                본문과 같은 회색 13px 라 "핵심 사실(언제·어디)"임이 드러나지 않았다.
                기간은 formatFestivalPeriodWithYear 로 연도까지 표기 — 기존 상세는 "1.9 — 1.25" 라
                종료된 축제의 연도를 알 수 없었고 목록 카드 표기와도 어깽났다. */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mb: '24px' }}>
              {formatFestivalPeriodWithYear(festival.startDate, festival.endDate) && (
                <Typography
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: 12.5,
                    lineHeight: 1.5,
                    color: INK_MID,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 99,
                    px: '11px',
                    py: '5px',
                  }}
                >
                  📅 {formatFestivalPeriodWithYear(festival.startDate, festival.endDate)}
                </Typography>
              )}
              {formatFestivalLocation(festival.addr1) && (
                <Typography
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: 12.5,
                    lineHeight: 1.5,
                    color: INK_MID,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 99,
                    px: '11px',
                    py: '5px',
                  }}
                >
                  📍 {festival.addr1 ?? formatFestivalLocation(festival.addr1)}
                </Typography>
              )}
            </Box>

            {festival.overview && (
              <Box
                sx={{
                  bgcolor: PAPER0,
                  border: `1px solid ${BORDER}`,
                  borderRadius: '14px',
                  p: '20px 22px',
                }}
              >
                <Typography
                  component="h2"
                  sx={{ fontSize: 13, fontWeight: 800, color: INK_HI, mb: '10px' }}
                >
                  어떤 축제인가
                </Typography>
                <Typography
                  sx={{
                    // [수정: 디자인 개편 2026-09-20] 이 페이지에서 가장 긴 읽기 문단이다 —
                    // 한글 장문 기준으로 13/1.7 은 빡빡해서 크기·행간을 올렸다.
                    fontSize: 14,
                    color: INK_MID,
                    lineHeight: 1.85,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {festival.overview}
                </Typography>
              </Box>
            )}

            {/* [수정: 디자인 개편 2026-09-20] 회색 면 박스라 본문 카드와 비슷한 무게로 읽혔다.
                면을 빼고 좌측 바만 남겨 "출처 주석" 임을 명확히 한다. */}
            <Box
              sx={{
                mt: '12px',
                pl: '12px',
                borderLeft: `2px solid ${BORDER}`,
              }}
            >
              <Typography sx={{ fontSize: 11.5, color: INK_LOW, lineHeight: 1.65 }}>
                본 정보는 한국관광공사 TourAPI 원본 데이터를 기반으로 하며, 실제 일정·장소는 주최 측
                사정에 따라 달라질 수 있어요.
              </Typography>
            </Box>

            {/* [수정: 2026-09-21] "이 축제로 여행 폴리오 만들기" CTA 제거(링크가 /chat 고정이라 축제 맥락이 전달되지 않던 오도 UX).
                대체 CTA 없이 그 자리에 축제 댓글 섹션을 둔다(festival-comments Spec D-10).
                festival 로딩 성공 분기 안에서만 마운트되므로 로딩/404/오류에서는 댓글 요청이 발생하지 않는다.
                CommentSection 자체 상단 여백(spacing 4=16) + 이 래퍼(spacing 3=12) = 옛 CTA 상단 간격(28)과 동일. */}
            <Box sx={{ mt: `${tokens.spacing[3]}px` }}>
              <FestivalCommentSection festivalContentId={festival.contentId} />
            </Box>
          </>
        )}
      </Box>
    </>
  );
}
